/** Typed client for the DSM public API (server/idserver/api_public.py). */

import type { AuthMode } from "./config";
import type { EditCatalog, Recipe } from "./editor/types";

/** Account tier (server tiers.py): test = renders cost nothing; plus / gold = more free tokens a day + queue priority. */
export type Tier = "normal" | "test" | "plus" | "gold";

/**
 * A premium model this account may pick in "5. Chỉnh sửa nâng cao" (server premium.py): name, one sentence and the
 * price, nothing technical. Paid ONLY from the top-up wallet.
 */
export interface PremiumModel {
  key: string;
  label: string;
  description: string;
  /** the consent box ("gửi ảnh tới …") is remembered per provider in this browser */
  provider: string;
  provider_label: string;
  /** top-up tokens per edit (0 for a test account) */
  price_tokens: number;
  /** false: shown greyed, "Tạm thời không dùng được" (budget reached, paused) */
  available: boolean;
}

export interface Me {
  daily: number;
  earned: number;
  total: number;
  next_reset_at: string;
  ads_left_today: number;
  earned_cap: number;
  cost_per_job: number;
  /** "Chỉnh sửa nâng cao" (queue "ai"); missing on an older server */
  cost_ai?: number;
  /** tier "test": renders cost nothing (both costs are 0) */
  unmetered?: boolean;
  /** tokens added by the operator: never reset, spent after the free and the ad tokens */
  topup?: number;
  tier?: Tier;
  /** end of the membership; null = no end (or tier normal) */
  tier_until?: string | null;
  /** what the free wallet is refilled to at 0h (5 normal, 200 plus, 500 gold) */
  daily_allowance?: number;
  /** may open /admin */
  is_admin?: boolean;
  /** premium models for this tier (Plus / Gold); empty or missing = the "Model" choice is not shown */
  premium_models?: PremiumModel[];
  /** v4 GĐ5: a photo studio account ("Logo tiệm" in /chinh-anh) */
  studio?: boolean;
  /** v4 "Giới thiệu bạn bè": tokens each side gets; 0 or missing = programme off (no menu item) */
  referral_reward?: number;
  locked_until: string | null;
  render_online: boolean;
  csrf_token: string | null;
}

export interface Spec {
  key: string;
  label: string;
  width_mm: number;
  height_mm: number;
  dpi: number;
  backgrounds: string[];
  allow_ai_edits: boolean;
}

export interface Background {
  key: string;
  label: string;
  rgb: [number, number, number] | null;
  class: string | null;
}

export interface Slider {
  key: string;
  min: number;
  max: number;
  step: number;
  default: number;
}

export interface Choice {
  key: string;
  label: string;
}

export interface AiPreset {
  key: string;
  label: string;
  background: string;
  expression: string;
  relight: boolean;
  lighting: string;
  style: string;
  tidy_hair: boolean;
  remove_glasses: boolean;
  outfits: Record<string, string>; // person group -> outfit key
}

/** catalog.json "ai": the desktop "AI chỉnh sửa trực tiếp" options and labels. */
export interface AiCatalog {
  groups: { key: string; label: string; outfits: string[] }[];
  outfits: Record<string, string>; // key -> label, incl. "keep" and "custom"
  outfit_text_max: number;
  expressions: Choice[];
  lights: Choice[];
  styles: Choice[];
  presets: AiPreset[];
}

/** What the web sends for "Chỉnh sửa nâng cao" (server AiEditIn). */
export interface AiEdit {
  frontal: boolean;
  expression: string;
  relight: boolean;
  lighting: string;
  style: string;
  outfit: string;
  outfit_text: string;
  tidy_hair: boolean;
  remove_glasses: boolean;
  /** a premium model's key (queue "cloud", top-up tokens); absent = the standard edit */
  model?: string;
}

export interface Catalog {
  specs: Spec[];
  backgrounds: Background[];
  light_modes: string[];
  light_filters: string[];
  skin_modes: string[];
  beauty_items: string[];
  geometry_items: string[];
  /** items whose target colour may be chosen, with the default colour (newer catalogues) */
  beauty_colours?: Record<string, [number, number, number]>;
  /** "Tự động + chỉnh" sliders (light_mode auto_plus): deltas added to the automatic light */
  light_adjust?: Slider[];
  /** skin_mode custom */
  skin_strength?: Omit<Slider, "key">;
  /** "Chỉnh sửa nâng cao" (newer catalogues) */
  ai?: AiCatalog;
  /** print sheet sizes (newer catalogues; web/src/sheet.ts has the same list as a fallback) */
  papers?: { key: string; label: string; width_mm: number; height_mm: number }[];
}

export interface JobSettings {
  spec: string;
  background: string;
  custom_rgb?: [number, number, number] | null;
  light_mode: string;
  light_filter: string;
  skin_mode: string;
  /** only with skin_mode "custom" */
  skin_strength?: number;
  /** only with light_mode "auto_plus": slider key -> delta (0 = unchanged) */
  light_adjust?: Record<string, number>;
  beauty: Record<string, number>;
  /** chosen target colour per item of `Catalog.beauty_colours` (none = the default colour) */
  beauty_rgb?: Record<string, [number, number, number]>;
  enhance: boolean;
  /** present = "Chỉnh sửa nâng cao" (queue "ai", costs Me.cost_ai) */
  ai?: AiEdit;
  /** merged page GĐ4: more specs from the same render (at most 3), one token each; sent only when not empty */
  extra_specs?: string[];
}

export type JobStatus = "queued" | "running" | "done" | "rejected" | "failed" | "cancelled";

export type QCLevel = "pass" | "warn" | "fail";

/** One check of `core.qc` (code, level, Vietnamese message from the pipeline). */
export interface QCItem {
  code: string;
  level: QCLevel;
  message: string;
  value?: number | null;
  expected?: string;
}

export interface QC {
  level?: QCLevel;
  items?: QCItem[];
  messages?: string[];
  sheet_paper?: string;
  /** GĐ4: the QC of each extra spec, in `settings.extra_specs` order (+ that spec's notes) */
  specs?: (QC & { spec: string; warnings?: string[] })[];
}

/** The contact buttons (server contact.py): a phone number for zalo.me, a Facebook page for m.me; null = no button. */
export interface Contact {
  zalo: string | null;
  messenger: string | null;
}

export interface Status {
  render_online: boolean;
  queue_length: number;
  contact?: Contact;
  /** v4: queue → a render machine serves it now (server availability.py); missing on an older server (web/src/queues.ts) */
  queues?: Partial<Record<string, boolean>>;
}

export interface Job {
  id: string;
  status: JobStatus;
  queue: string;
  settings: JobSettings;
  progress: { stage?: string; frac?: number } | null;
  reject_reason: string | null;
  warnings: string[];
  qc: QC | null;
  identity_score: number | null;
  /** signed links when done; `crop` = the photo cut like the result before any edit (newer render machines);
   * `local` = an AI job's photo processed without AI (render machines from 2026-10-24); `size_1`… = "Khổ in" (one
   * JPEG per print size), `sizes_zip` = all of them + the order note */
  files: { photo?: string; sheet?: string; crop?: string; local?: string; export?: string; sheet_jpg?: string;
           sizes_zip?: string; [size: `size_${number}`]: string | undefined;
           /** GĐ4: the 2nd … 4th spec's photo / crop / photo without AI */
           [spec: `${"photo" | "crop" | "local"}_${number}`]: string | undefined };
  created_at: string;
  finished_at: string | null;
  expires_at: string;
  created?: boolean;
  /** the user's own rating of this photo */
  feedback?: Feedback | null;
  /** v4, while queued: jobs a render machine takes first, seconds until done (null: not known yet) */
  queue_position?: number | null;
  eta_s?: number | null;
}

export interface Feedback {
  rating: number;
  comment: string | null;
  updated_at: string;
}

// ---- /api/admin (server/idserver/api_admin.py) ----

export type Plan = "week" | "month" | "year" | "days" | "forever";

export interface AdminUser {
  id: string;
  email: string | null;
  name: string | null;
  tier: Tier;
  effective_tier: Tier;
  tier_until: string | null;
  tier_note: string | null;
  daily: number;
  earned: number;
  topup: number;
  total: number;
  daily_allowance: number;
  created_at: string;
  last_login_at: string | null;
  locked_until: string | null;
  jobs_total: number;
  last_job_at: string | null;
  is_admin: boolean;
  /** v4 GĐ5: photo studio (may upload its logo and stamp it) */
  studio?: boolean;
}

export interface AdminLedger {
  wallet: "daily" | "earned" | "topup";
  delta: number;
  reason: string;
  source: string | null;
  note: string | null;
  created_at: string;
}

export interface AdminUserDetail extends AdminUser {
  ledger: AdminLedger[];
  jobs: { id: string; created_at: string; queue: string; status: JobStatus; rating: number | null }[];
  /** v4 "Giới thiệu bạn bè": who invited this account, how many it invited (of them, rewarded) */
  referred_by?: { id: string; email: string | null; name: string | null } | null;
  referrals_invited?: number;
  referrals_rewarded?: number;
}

/** GET /api/referrals (server referrals.py): this account's link and what it brought. */
export interface Referrals {
  enabled: boolean;
  code: string | null;
  /** "/?ref=<code>": the page puts its own origin in front */
  link: string | null;
  /** accounts created through the link */
  invited: number;
  /** of them, rewarded (their first photo is done) */
  rewarded: number;
  /** every referral token this account got (inviting and being invited) */
  tokens_earned: number;
  /** tokens for each side per referral */
  reward: number;
  max_per_month: number;
  rewarded_this_month: number;
  earned_cap: number;
}

export interface AdminFeedbackItem {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  updated_at: string;
  user_id: string;
  user_email: string | null;
  user_name: string | null;
  job_id: string;
  job_status: JobStatus;
  queue: string;
  spec: string | null;
  background: string | null;
  ai: AiEdit | null;
}

export interface AdminFeedbackPage {
  count: number;
  average: number | null;
  by_queue: Record<string, { count: number; average: number | null }>;
  stars: Record<string, number>;
  total: number;
  items: AdminFeedbackItem[];
}

export interface AdminContact extends Contact {
  updated_at: string | null;
  updated_by: string | null;
}

// ---- /api/admin/ai: premium models (server premium.py) ----

export interface AiSettings {
  usd_vnd: number;
  token_vnd: number;
  margin: number;
  global_monthly_budget_usd: number;
}

export interface AdminProvider {
  key: string;
  label: string;
  enabled: boolean;
  has_key: boolean;
  /** the only part of the key that ever leaves the server */
  key_last4: string | null;
  /** false: the server's secret key changed, paste the API key again */
  key_readable: boolean | null;
  extra: Record<string, string | number | boolean>;
  monthly_budget_usd: number | null;
  month_spend_usd: number;
  updated_at: string | null;
  updated_by: string | null;
}

export type ModelStatus = "available" | "disabled" | "provider_off" | "no_key" | "paused" | "provider_budget" | "global_budget";

export interface AdminModel {
  key: string;
  provider: string;
  model_id: string;
  label: string;
  description: string;
  enabled: boolean;
  tiers: Tier[];
  params: Record<string, unknown>;
  cost_usd_est: number;
  price_tokens: number;
  suggested_price: number;
  daily_cap: number | null;
  sort: number;
  paused_until: string | null;
  error_streak: number;
  status: ModelStatus;
  updated_at: string | null;
  updated_by: string | null;
}

export interface AdminAi {
  settings: AiSettings;
  month_spend_usd: number;
  month_start: string;
  fake_providers: boolean;
  /** false: IDSERVER_SECRET_KEY is empty, keys cannot be stored */
  key_storage_ready: boolean;
  providers: AdminProvider[];
  models: AdminModel[];
}

export interface ProviderChange {
  enabled?: boolean;
  label?: string;
  /** write-only */
  api_key?: string;
  clear_key?: boolean;
  extra?: Record<string, string | number | boolean>;
  monthly_budget_usd?: number | null;
}

export interface ModelChange {
  provider?: string;
  model_id?: string;
  label?: string;
  description?: string;
  enabled?: boolean;
  tiers?: Tier[];
  params?: Record<string, unknown>;
  cost_usd_est?: number;
  price_tokens?: number;
  daily_cap?: number | null;
  sort?: number;
  /** clear a pause after an error streak */
  resume?: boolean;
}

export interface AdminAiModelStats {
  /** "" = the standard edit on the render machine */
  key: string;
  label: string;
  calls: number;
  ok: number;
  errors: number;
  refused: number;
  blocked: number;
  cost_usd: number;
  avg_latency_ms: number | null;
  jobs: number;
  jobs_done: number;
  /** refused by the render machine's checks (face changed, unsafe output…) */
  jobs_ai_failed: number;
  rating_count: number;
  rating_avg: number | null;
}

export interface AdminAiStats {
  days: number;
  since: string;
  month_spend_usd: number;
  month_spend_by_provider: Record<string, number>;
  global_monthly_budget_usd: number;
  models: AdminAiModelStats[];
  daily: { date: string; model_key: string; calls: number; cost_usd: number }[];
}

export interface TierChange {
  tier: Tier;
  plan: Plan;
  days?: number;
  note?: string;
}

export interface AdSession {
  session_id: string;
  nonce: string;
  expires_at: string;
  min_seconds: number;
}

// ---- v4 "Chỉnh ảnh" (server/idserver/api_edit.py; types of the catalogue and recipe in src/editor/types.ts) ----


/** The editor's working copy on the server (≤ 2048 px): uploaded once, previewed many times. */
export interface EditSource {
  id: string;
  width: number;
  height: number;
  expires_at: string;
}

export type PreviewWant = "lut" | "base" | "auto";

/** A preview from the render machine: LUT strip (global ops) and / or proxy picture (crop + spatial ops). */
export interface EditPreview {
  id: string;
  status: "queued" | "running" | "done" | "failed";
  files: { lut?: string; base?: string };
  auto: Record<string, number> | null;
  error: string | null;
  ms: Record<string, number> | null;
  /** GĐ2: face steps the render machine could not do as asked (face_not_found, face_step_dropped:<op>…) */
  warnings?: string[];
}

export interface EditAsset {
  ref: string;
  kind: string;
  slug: string;
  version: number;
  name: string;
  group: string;
  tags: string[];
  tier: "free" | "plus" | "gold";
  /** thumb / lut; GĐ4 also by file name: font.ttf, ofl.txt, web-frame-3x2.png, web-overlay.png… */
  files: { thumb?: string; lut?: string } & Record<string, string | undefined>;
  /** GĐ4: font metrics, template fields + layout, frame ratios, overlay defaults (server asset_layers.public_meta) */
  meta?: Record<string, unknown>;
}

export interface AdminAsset extends EditAsset {
  status: "draft" | "approved" | "published" | "retired";
  manifest: Record<string, unknown>;
  created_at: string;
  created_by: string | null;
  approved_at: string | null;
  approved_by: string | null;
  published_at: string | null;
  published_by: string | null;
}

// ---- v4 GĐ5 "Hàng loạt & in" (server/idserver/api_batch.py, api_studio.py) ----

/** One photo of a batch: "uploaded" before the batch starts, then its job's status. */
export interface EditBatchItem {
  index: number;
  name: string;
  status: JobStatus | "uploaded";
  job_id: string | null;
  reject_reason: string | null;
  progress: { stage?: string; frac?: number } | null;
  files: { photo?: string; export?: string; sheet?: string; sheet_jpg?: string };
  /** tokens taken for this photo; given back when `refunded` */
  charged: number;
  refunded: boolean;
  warnings: string[];
}

export interface EditBatch {
  id: string;
  status: "open" | "started" | "cancelled";
  count: number;
  uploaded: number;
  cost_per_photo: number;
  /** open: what starting will cost; started: what was taken */
  total_cost: number;
  refunded: number;
  done: number;
  failed: number;
  waiting: number;
  finished: boolean;
  created_at: string;
  started_at: string | null;
  expires_at: string;
  items: EditBatchItem[];
  /** the batch's shared print sheets (jobs of mode "sheet") */
  sheets: Job[];
  /** signed link to every finished file in one ZIP */
  zip: string | null;
  created?: boolean | null;
  /** v4, while photos wait: jobs taken before the batch's first one, seconds until its last one is done */
  queue_position?: number | null;
  eta_s?: number | null;
}

/** A studio's own logo: private, only this account sees and uses it. */
export interface StudioLogo {
  ref: string;
  slug: string;
  name: string;
  width: number;
  height: number;
  files: { logo?: string; thumb?: string };
  created_at: string;
}

export const TERMINAL: JobStatus[] = ["done", "rejected", "failed", "cancelled"];

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, readonly detail: Record<string, unknown> = {}) {
    super(`${status} ${code}`);
  }
}

export interface ApiOptions {
  base: string;
  authMode: AuthMode;
  /** dev mode: the X-Dev-User name, null = signed out (guest) until `loginDev`. */
  devUser?: () => string | null;
  turnstile?: () => Promise<string | null>;
  fetchImpl?: typeof fetch;
}

export function createApi(opts: ApiOptions) {
  let csrf: string | null = null;
  let devName: string | null | undefined; // set by loginDev; undefined = ask opts.devUser
  const doFetch = opts.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));

  /** A file the operator may see before it is public (dev sign-in is a header, so <img src> alone cannot load it). */
  async function blob(path: string): Promise<Blob> {
    const headers = new Headers();
    const dev = devName !== undefined ? devName : opts.devUser?.();
    if (opts.authMode === "dev" && dev) headers.set("X-Dev-User", encodeURIComponent(dev));
    const resp = await doFetch(`${opts.base}${path}`, { headers, credentials: "include" });
    if (!resp.ok) throw new ApiError(resp.status, resp.status >= 500 ? "server_error" : "file_not_found");
    return resp.blob();
  }

  async function request<T>(path: string, init: RequestInit & { turnstile?: boolean } = {}): Promise<T> {
    const headers = new Headers(init.headers);
    const dev = devName !== undefined ? devName : opts.devUser?.();
    // header values must be Latin-1: "Nguyễn Văn An" would make fetch throw; ASCII names ("web-dev") are unchanged
    if (opts.authMode === "dev" && dev) headers.set("X-Dev-User", encodeURIComponent(dev));
    const method = (init.method ?? "GET").toUpperCase();
    if (method !== "GET" && csrf) headers.set("X-CSRF-Token", csrf);
    if (init.turnstile && opts.turnstile) {
      const token = await opts.turnstile();
      if (token) headers.set("CF-Turnstile-Token", token);
    }
    const resp = await doFetch(`${opts.base}${path}`, { ...init, headers, credentials: "include" });
    if (resp.status === 204) return undefined as T;
    let body: unknown = null;
    try {
      body = await resp.json();
    } catch {
      body = null;
    }
    if (!resp.ok) {
      const detail = (body as { detail?: unknown } | null)?.detail;
      if (detail && typeof detail === "object" && "error" in detail) {
        const d = detail as Record<string, unknown>;
        throw new ApiError(resp.status, String(d.error), d);
      }
      throw new ApiError(resp.status, resp.status >= 500 ? "server_error" : "http_error");
    }
    return body as T;
  }

  const json = (method: string, body: unknown, extra: { turnstile?: boolean } = {}) =>
    ({ method, body: JSON.stringify(body), headers: { "Content-Type": "application/json" }, ...extra });
  const query = (params: Record<string, string | number | undefined | null>) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
    const s = q.toString();
    return s ? `?${s}` : "";
  };

  function remember(me: Me): Me {
    if (me.csrf_token) csrf = me.csrf_token;
    return me;
  }

  return {
    /**
     * False when this browser surely has no session, so a visitor's first page load needs no /api/me (and logs no
     * 401): dev mode without a user name, or a same-origin site without the readable CSRF cookie that the server sets
     * next to the session cookie at sign-in. Another API origin cannot be checked here: true.
     */
    maybeSignedIn: () => {
      if (opts.authMode === "dev") return Boolean(devName !== undefined ? devName : opts.devUser?.());
      if (opts.base) return true;
      return /(?:^|;\s*)idcsrf=/.test(document.cookie);
    },
    fileUrl: (path: string) => `${opts.base}${path}`,
    me: async () => remember(await request<Me>("/api/me")),
    specs: () => request<Catalog>("/api/specs"),
    status: () => request<Status>("/api/status"),
    /** `ref`: a friend's referral code (referral.ts), recorded only when this sign-in creates the account. */
    loginGoogle: async (idToken: string, ref?: string | null) =>
      remember(await request<Me>("/api/auth/google", json("POST", ref ? { id_token: idToken, ref } : { id_token: idToken }))),
    /** Dev server only (IDSERVER_AUTH_MODE=dev): sign in as `name` by sending it as X-Dev-User from now on; with a
     * referral code, through /api/auth/dev (creates the account with it, like a first Google sign-in). */
    loginDev: async (name: string, ref?: string | null) => {
      devName = name;
      return remember(await (ref ? request<Me>("/api/auth/dev", json("POST", { ref })) : request<Me>("/api/me")));
    },
    /** v4 "Giới thiệu bạn bè": the link, how many friends signed up through it, tokens received. */
    referrals: () => request<Referrals>("/api/referrals"),
    logout: async () => {
      await request<void>("/api/auth/logout", { method: "POST" });
      csrf = null;
      devName = null;
    },
    createJob: (photo: Blob, settings: JobSettings, idempotencyKey: string) => {
      const form = new FormData();
      form.append("settings", JSON.stringify(settings));
      form.append("file", photo, "photo.jpg");
      return request<Job>("/api/jobs", { method: "POST", body: form, turnstile: true,
                                         headers: { "Idempotency-Key": idempotencyKey } });
    },
    job: (id: string) => request<Job>(`/api/jobs/${encodeURIComponent(id)}`),
    jobs: () => request<Job[]>("/api/jobs"),
    deleteJob: (id: string) => request<Job>(`/api/jobs/${encodeURIComponent(id)}`, { method: "DELETE" }),
    openAd: () => request<AdSession>("/api/ads/sessions", { method: "POST", turnstile: true }),
    claimAd: (sessionId: string, nonce: string) =>
      request<{ daily: number; earned: number; already_granted: boolean }>(
        `/api/ads/sessions/${encodeURIComponent(sessionId)}/claim`,
        { method: "POST", body: JSON.stringify({ nonce }), headers: { "Content-Type": "application/json" } }),
    /** Rate a finished photo (1-5 stars + optional comment); sending again changes the rating. */
    rateJob: (id: string, rating: number, comment: string) =>
      request<Feedback>(`/api/jobs/${encodeURIComponent(id)}/feedback`, json("PUT", { rating, comment }, { turnstile: true })),
    // ---- v4 "Chỉnh ảnh": guests may upload a working copy and preview; exporting needs an account ----
    editCatalog: () => request<EditCatalog>("/api/edit/catalog"),
    editSource: (photo: Blob) => {
      const form = new FormData();
      form.append("file", photo, "photo.jpg");
      return request<EditSource>("/api/edit/sources", { method: "POST", body: form, turnstile: true });
    },
    /** The server waits up to ~3 s for the render machine: `status` is "done" in the same answer when it is quick. */
    editPreview: (body: { source_id: string; recipe: Recipe; want: PreviewWant[]; full_width?: number }) =>
      request<EditPreview>("/api/edit/preview", json("POST", body)),
    editPreviewGet: (id: string) => request<EditPreview>(`/api/edit/preview/${encodeURIComponent(id)}`),
    /** `model` (GĐ3): a premium model for the recipe's AI steps (queue "edit-cloud", top-up tokens only). */
    createEditJob: (photo: Blob, recipe: Recipe, idempotencyKey: string, model?: string) => {
      const form = new FormData();
      form.append("recipe", JSON.stringify(recipe));
      if (model) form.append("model", model);
      form.append("file", photo, "photo.jpg");
      return request<Job>("/api/edit/jobs", { method: "POST", body: form, turnstile: true,
                                              headers: { "Idempotency-Key": idempotencyKey } });
    },
    /** v4 "Kích nét" (queue edit-sharpen, catalogue `pricing.sharpen` tokens): the full photo sharpened before editing;
     * the result (`files.export`, full size) + a report in `qc.report` (see editor/SharpenCard.tsx). */
    createSharpenJob: (photo: Blob, idempotencyKey: string) => {
      const form = new FormData();
      form.append("file", photo, "photo.jpg");
      return request<Job>("/api/edit/sharpen", { method: "POST", body: form, turnstile: true,
                                                 headers: { "Idempotency-Key": idempotencyKey } });
    },
    /** "Làm đẹp nhanh": the recipe at every level of the catalogue's profiles.beauty, one job (queue "edit"),
     * `pricing.variants` tokens; files variant_1…n (full-size JPEG) + photo (the levels side by side). */
    createVariantsJob: (photo: Blob, recipe: Recipe, idempotencyKey: string) => {
      const form = new FormData();
      form.append("recipe", JSON.stringify(recipe));
      form.append("file", photo, "photo.jpg");
      return request<Job>("/api/edit/variants", { method: "POST", body: form, turnstile: true,
                                                  headers: { "Idempotency-Key": idempotencyKey } });
    },
    editJobs: () => request<Job[]>("/api/edit/jobs"),
    editJob: (id: string) => request<Job>(`/api/edit/jobs/${encodeURIComponent(id)}`),
    assets: (kind: string) => request<{ items: EditAsset[] }>(`/api/assets${query({ kind })}`),
    // ---- v4 GĐ5: batches (one recipe, many photos) and the studio's logos ----
    createBatch: (recipe: Recipe, count: number, ref: { width: number; height: number }, idempotencyKey: string) =>
      request<EditBatch>("/api/edit/batches", { ...json("POST", { recipe, count, ref }, { turnstile: true }),
                                                headers: { "Content-Type": "application/json", "Idempotency-Key": idempotencyKey } }),
    uploadBatchItem: (id: string, index: number, photo: Blob, name: string) => {
      const form = new FormData();
      form.append("name", name);
      form.append("file", photo, "photo.jpg");
      return request<EditBatchItem>(`/api/edit/batches/${encodeURIComponent(id)}/items/${index}`, { method: "PUT", body: form });
    },
    startBatch: (id: string) => request<EditBatch>(`/api/edit/batches/${encodeURIComponent(id)}/start`, { method: "POST" }),
    batch: (id: string) => request<EditBatch>(`/api/edit/batches/${encodeURIComponent(id)}`),
    batches: () => request<EditBatch[]>("/api/edit/batches"),
    deleteBatch: (id: string) => request<EditBatch>(`/api/edit/batches/${encodeURIComponent(id)}`, { method: "DELETE" }),
    batchSheet: (id: string, sheet: Record<string, unknown>, idempotencyKey: string) =>
      request<Job>(`/api/edit/batches/${encodeURIComponent(id)}/sheet`,
                   { ...json("POST", { sheet }), headers: { "Content-Type": "application/json", "Idempotency-Key": idempotencyKey } }),
    logos: () => request<{ items: StudioLogo[]; max: number }>("/api/edit/logos"),
    uploadLogo: (file: Blob, name: string) => {
      const form = new FormData();
      form.append("name", name);
      form.append("file", file, "logo.png");
      return request<StudioLogo>("/api/edit/logos", { method: "POST", body: form });
    },
    deleteLogo: (slug: string) => request<void>(`/api/edit/logos/${encodeURIComponent(slug)}`, { method: "DELETE" }),
    /** v4 item 8: an uncaught browser error (errorReport.ts) → server log (+ Sentry when the server has a DSN). */
    clientError: (report: ClientErrorReport) =>
      request<void>("/api/client-errors", { ...json("POST", report), keepalive: true }),
    admin: {
      users: (p: { q?: string; tier?: string; offset?: number } = {}) =>
        request<{ total: number; items: AdminUser[] }>(`/api/admin/users${query({ ...p, limit: 50 })}`),
      user: (id: string) => request<AdminUserDetail>(`/api/admin/users/${encodeURIComponent(id)}`),
      addTokens: (id: string, amount: number, note: string) =>
        request<AdminUserDetail>(`/api/admin/users/${encodeURIComponent(id)}/tokens`, json("POST", { amount, note })),
      setTier: (id: string, change: TierChange) =>
        request<AdminUserDetail>(`/api/admin/users/${encodeURIComponent(id)}/tier`, json("POST", change)),
      feedback: (p: { rating?: number | null; queue?: string; offset?: number } = {}) =>
        request<AdminFeedbackPage>(`/api/admin/feedback${query({ ...p, limit: 50 })}`),
      contact: () => request<AdminContact>("/api/admin/contact"),
      /** As typed in the form ("" removes that button); the server normalises and checks both. */
      saveContact: (values: { zalo: string; messenger: string }) =>
        request<AdminContact>("/api/admin/contact", json("PUT", values)),
      ai: () => request<AdminAi>("/api/admin/ai"),
      aiSettings: (values: Partial<AiSettings>) => request<AdminAi>("/api/admin/ai/settings", json("PUT", values)),
      aiProvider: (key: string, change: ProviderChange) =>
        request<AdminAi>(`/api/admin/ai/providers/${encodeURIComponent(key)}`, json("PUT", change)),
      aiProviderCheck: (key: string) =>
        request<{ ok: boolean; message: string; latency_ms: number }>(
          `/api/admin/ai/providers/${encodeURIComponent(key)}/check`, { method: "POST" }),
      aiModel: (key: string, change: ModelChange) =>
        request<AdminAi>(`/api/admin/ai/models/${encodeURIComponent(key)}`, json("PUT", change)),
      aiStats: (days = 30) => request<AdminAiStats>(`/api/admin/ai/stats${query({ days })}`),
      // ---- library assets (/admin › Tài sản) ----
      assets: (kind?: string) => request<AdminAsset[]>(`/api/admin/assets${query({ kind })}`),
      uploadAsset: (manifest: Record<string, unknown>, files: File[]) => {
        const form = new FormData();
        form.append("manifest", JSON.stringify(manifest));
        for (const f of files) form.append("files", f, f.name);
        return request<AdminAsset>("/api/admin/assets", { method: "POST", body: form });
      },
      assetFile: (path: string) => blob(path),
      /** v4 GĐ5: mark / unmark a photo studio ("Logo tiệm") */
      setStudio: (id: string, studio: boolean) =>
        request<AdminUserDetail>(`/api/admin/users/${encodeURIComponent(id)}/studio`, json("POST", { studio })),
      assetAction: (a: { kind: string; slug: string; version: number }, action: "approve" | "publish" | "retire" | "restore") =>
        request<AdminAsset>(`/api/admin/assets/${encodeURIComponent(a.kind)}/${encodeURIComponent(a.slug)}/${a.version}/${action}`,
                            { method: "POST" }),
      /** v4 item 8: /admin › Báo cáo (server reports.py), local days of the server's reset time zone */
      reports: (days = 30) => request<AdminReport>(`/api/admin/reports${query({ days })}`),
    },
  };
}

export type Api = ReturnType<typeof createApi>;

// ---- v4 item 8: /admin › Báo cáo (server reports.py) and browser error reports (server monitoring.py) ----

export interface AdminReportCounts {
  /** queue → status → jobs created that day (status as it is now) */
  jobs: Record<string, Record<string, number>>;
  statuses: Record<string, number>;
  jobs_total: number;
  /** wallet (daily = free, earned = ads, topup) → tokens taken for jobs */
  debited: Record<"daily" | "earned" | "topup", number>;
  debited_total: number;
  refunded: number;
  ad_tokens: number;
  /** "Giới thiệu bạn bè" rewards (inviter + invited, earned wallet); missing from an API older than the page */
  referral_tokens?: number;
  /** added (or taken back) in /admin */
  topup_added: number;
  new_users: number;
  /** accounts with a job; in the totals: distinct over the whole window */
  active_users: number;
  premium_calls: number;
  premium_cost_usd: number;
  feedback_count: number;
  feedback_avg: number | null;
}

export interface AdminReportDay extends AdminReportCounts {
  /** local day "2026-10-29" */
  date: string;
}

export interface AdminReport {
  days: number;
  since: string;
  until: string;
  tz: string;
  /** oldest first, one per day */
  series: AdminReportDay[];
  totals: AdminReportCounts & { refund_rate: number | null };
  queues: { queue: string; jobs: number; statuses: Record<string, number>; avg_process_s: number | null;
            avg_wait_s: number | null }[];
  /** the most frequent first */
  reasons: { status: string; reason: string; count: number; queues: Record<string, number> }[];
}

export interface ClientErrorReport {
  kind: "error" | "unhandledrejection" | "react";
  message: string;
  stack: string;
  /** the page's path only, never its query string */
  url: string;
}